/**
 * Formato de importes con moneda, en un solo lugar.
 *
 * `Intl.NumberFormat` sólo acepta códigos de exactamente tres letras: con un
 * código vacío, con un alias del backend ("$", "PESOS") o con cualquier cosa
 * malformada lanza `RangeError: Invalid currency code`. Cuando eso pasa dentro
 * de un `renderCell` no se rompe una celda: se cae la grilla entera.
 *
 * Acá el formateo nunca lanza, y además informa *por qué* no pudo usar la
 * moneda, para que la pantalla pueda marcar el dato en vez de disimularlo.
 */

/** Qué le pasa al código de moneda que vino en el dato. */
export type CurrencyIssue =
    /** No vino moneda. */
    | 'missing'
    /** Vino algo que no es un código ISO de tres letras. */
    | 'invalid';

export interface CurrencyInspection {
    /** Código ISO listo para Intl. Sólo viene cuando no hay problema. */
    code?: string;
    issue?: CurrencyIssue;
    /** Lo que mandó el backend, tal cual, para poder mostrarlo en el aviso. */
    raw?: string;
}

/**
 * Alias que manda el backend en lugar del código ISO. No es una lista de
 * conveniencia: son valores que existen en los datos y que, sin traducir,
 * harían fallar a Intl.
 */
const CURRENCY_ALIASES: Record<string, string> = {
    PESO: 'ARS',
    PESOS: 'ARS',
    '$': 'ARS',
    'AR$': 'ARS',
};

const ISO_CURRENCY_PATTERN = /^[A-Z]{3}$/;

export const normalizeCurrencyCode = (currencyCode: string | undefined | null): string | undefined => {
    const normalizedCurrency = currencyCode?.trim().toUpperCase();

    if (!normalizedCurrency) {
        return undefined;
    }

    return CURRENCY_ALIASES[normalizedCurrency] ?? normalizedCurrency;
};

/**
 * Clasifica el código de moneda sin formatear nada. Es lo que le permite a la
 * UI decidir si tiene que marcar el dato.
 */
export const inspectCurrency = (currencyCode: string | undefined | null): CurrencyInspection => {
    const raw = currencyCode?.trim();

    if (!raw) {
        return { issue: 'missing' };
    }

    const normalized = normalizeCurrencyCode(raw);

    if (!normalized || !ISO_CURRENCY_PATTERN.test(normalized)) {
        return { issue: 'invalid', raw };
    }

    return { code: normalized };
};

/** Importe sin símbolo, con la separación de miles y decimales de es-AR. */
export const formatPlainAmount = (amount: number | undefined | null): string =>
    (amount ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Importe formateado. Nunca lanza: sin una moneda usable devuelve el número
 * pelado, que es preferible a tumbar la pantalla.
 *
 * Devuelve sólo texto. Para que el problema de moneda se *vea*, usar el
 * componente `CurrencyAmount`, que agrega la marca visual.
 */
export const formatCurrencyAmount = (
    amount: number | undefined | null,
    currencyCode: string | undefined | null
): string => {
    const value = amount ?? 0;
    const { code } = inspectCurrency(currencyCode);

    if (!code) {
        return formatPlainAmount(value);
    }

    try {
        return value.toLocaleString('es-AR', { style: 'currency', currency: code });
    } catch {
        // Un código de tres letras que Intl igual rechace: se muestra el importe
        // con el código al lado, sin perder el dato.
        return `${formatPlainAmount(value)} ${code}`;
    }
};

/**
 * Igual que `formatCurrencyAmount`, pero deja escrito el problema de moneda en
 * el propio texto. Es para los lugares donde el importe se arma como string y
 * no puede montarse el componente `CurrencyAmount`: sin esta marca, un importe
 * sin símbolo es indistinguible de uno bien formateado.
 */
export const formatCurrencyAmountWithIssue = (
    amount: number | undefined | null,
    currencyCode: string | undefined | null
): string => {
    const inspection = inspectCurrency(currencyCode);
    const text = formatCurrencyAmount(amount, currencyCode);

    if (!inspection.issue) {
        return text;
    }

    return `${text} ⚠ ${shortCurrencyIssueLabel(inspection)}`;
};

/** Texto del aviso que acompaña a un importe cuya moneda no se pudo resolver. */
export const describeCurrencyIssue = (inspection: CurrencyInspection): string | undefined => {
    if (inspection.issue === 'missing') {
        return 'Sin moneda informada: el importe se muestra sin símbolo.';
    }

    if (inspection.issue === 'invalid') {
        return `Moneda no reconocida ("${inspection.raw}"): el importe se muestra sin símbolo.`;
    }

    return undefined;
};

/** Etiqueta corta para la marca visual, al lado del importe. */
export const shortCurrencyIssueLabel = (inspection: CurrencyInspection): string | undefined => {
    if (inspection.issue === 'missing') return 'sin moneda';
    if (inspection.issue === 'invalid') return inspection.raw;
    return undefined;
};
